import React from 'react';

import PropTypes from 'prop-types';

import { formatCurrency } from '@atc/string-fns';

import { getIncentiveValue, hasIncentiveCashValue } from 'reaxl-wallet';

import PriceLineItem from '@/components/PriceLineItem';

/**
 *  Breaks pricing data into a user friendly data table.
 *
 */
function PricingBreakdown({
    data: {
        dealerDiscountedPrice,
        incentive,
        msrp,
        reductions = [],
        salePrice,
        discounts = [],
        isMismatch,
    } = {},
    showDealerAdjustment,
    appliedIncentives = [],
    activeDealOffer = {},
    enableOfferBreakdownIncentives = false, // if true, show price/offer breakdown in new price section
    priceLabel,
    uiContext,
    ...rest
}) {
    if (!msrp && !salePrice && !incentive && uiContext !== 'price-section') {
        return <div className="text-size-500">Call Dealer For Price</div>;
    }

    const renderPriceLineItem = ({ label, value, priceClassName, tooltip, ...rowRest }) => value && (
        <PriceLineItem
            label={label}
            value={value}
            priceClassName={priceClassName}
            tooltip={tooltip}
            containerClassName="iris:py-1"
            {...rowRest}
        />
    );

    const priceMismatch = !!isMismatch;
    const dealerAskingPrice = dealerDiscountedPrice || salePrice;
    const showDealerAskingPrice = !!dealerAskingPrice && !priceMismatch && (!incentive || dealerAskingPrice !== incentive);

    /* for visibility of dealer additions that raise price above msrp */
    const dealerAdjustment = incentive > msrp && showDealerAdjustment;
    const totalDiscounts = discounts?.reduce((sum, currentValue) => sum + currentValue.value, 0) || 0;
    const totalReductions = reductions?.reduce((sum, currentValue) => sum + currentValue.value, 0) || 0;
    /* incentive price already includes discounts & reductions = removal for accurate adjust */
    const dealerAdjustmentValue = incentive - msrp - (!!discounts && -totalDiscounts) - (!!reductions && -totalReductions);
    const enableDealerAdjustment = dealerAdjustment && dealerAdjustmentValue > 0;

    // for offer breakdown incentives and discounts
    const specialOfferValue = activeDealOffer?.pricingDetails?.specialOfferValue || 0;
    const manufacturerOfferValue = activeDealOffer?.manufacturerOffer || activeDealOffer?.pricingDetails?.rebate || 0;
    const hasManufacturerOffer = !!(enableOfferBreakdownIncentives && manufacturerOfferValue);
    const showManufacturerOffer = !!(hasManufacturerOffer && (appliedIncentives.length === 0 || !appliedIncentives.some(hasIncentiveCashValue)));

    const renderAppliedIncentives = () => {
        if (appliedIncentives.length === 0) return null;
        return appliedIncentives.map((inc) => getIncentiveValue(inc) > 0 && renderPriceLineItem({
            key: inc.id,
            label: `${inc.name}`,
            value: `- ${formatCurrency(getIncentiveValue(inc))}`,
        }));
    };

    const renderSpecialOffer = () => {
        if (!specialOfferValue) return null;
        const isAdjustment = specialOfferValue < 0;
        return renderPriceLineItem({
            label: isAdjustment ? 'Dealer Adjustment' : 'Dealer Discount',
            value: `${isAdjustment ? '+' : '-'} ${formatCurrency(Math.abs(specialOfferValue))}`,
        });
    };

    // simplified price breakdown for new price section in VDP displaying offer/incentives if enabled
    if (uiContext === 'price-section') {
        const listingPrice = activeDealOffer?.pricingDetails?.salePrice || incentive || msrp;
        return (
            <div
                data-cmp="pricingBreakdown"
                {...rest}
            >
                {enableOfferBreakdownIncentives && (
                    <>
                        {msrp && renderPriceLineItem({
                            label: priceLabel,
                            value: formatCurrency(msrp),
                        })}
                        {renderAppliedIncentives()}
                        {showManufacturerOffer && renderPriceLineItem({
                            label: 'Manufacturer Offer',
                            value: `- ${formatCurrency(manufacturerOfferValue)}`,
                        })}
                        {renderSpecialOffer()}
                    </>
                )}
                {listingPrice && renderPriceLineItem({
                    label: 'Listing Price',
                    value: formatCurrency(listingPrice),
                    priceClassName: 'text-bold',
                })}
            </div>
        );
    }

    return (
        <div
            data-cmp="pricingBreakdown"
            {...rest}
        >
            {msrp && renderPriceLineItem({
                label: 'MSRP',
                value: formatCurrency(msrp),
            })}

            {reductions && reductions.map((reduction, index) => reduction.label.startsWith('Dealer') && renderPriceLineItem({
                key: reduction.label + reduction.value,
                label: reduction.label,
                value: `${reduction.label.includes('Add-on') ? '+' : '-'}${formatCurrency(reduction.value)}`,
                tooltip: reduction.tooltip,
            }))}

            {msrp && (showDealerAskingPrice || enableDealerAdjustment) && <hr className="margin-vertical-2" />}

            {showDealerAskingPrice && renderPriceLineItem({
                label: 'Dealer Asking Price',
                value: formatCurrency(dealerAskingPrice),
                priceClassName: 'text-bold',
            })}

            {discounts && discounts.map((discount, index) => renderPriceLineItem({
                key: discount.label + discount.value,
                label: `${discount.label} ${discount.expiration ? `(expires ${discount.expiration})` : ''}`,
                value: `-${formatCurrency(discount.value)}`,
                tooltip: discount.tooltip,
            }))}

            {reductions && reductions.map((reduction, index) => (reduction.label.startsWith('Special') || reduction.label.startsWith('Applied')) && renderPriceLineItem({
                key: reduction.label + reduction.value,
                label: reduction.label,
                value: `${reduction.label.includes('Add-on') ? '+' : '-'}${formatCurrency(reduction.value)}`,
                tooltip: reduction.tooltip,
            }))}

            {enableDealerAdjustment && renderPriceLineItem({
                label: 'Dealer Adjustment',
                value: formatCurrency(dealerAdjustmentValue),
            })}

            {incentive && (showDealerAskingPrice || (msrp && !showDealerAskingPrice)) && <hr className="margin-vertical-2" />}

            {incentive && renderPriceLineItem({
                label: 'Listing Price',
                value: formatCurrency(incentive),
            })}
        </div>
    );
}

PricingBreakdown.propTypes /* remove-proptypes */ = {
    /*
    *   A standard axl price data object with msrp and salePrice and an array of discount objects
    */
    data: PropTypes.shape({
        dealerDiscountedPrice: PropTypes.number,
        incentive: PropTypes.number,
        msrp: PropTypes.number,
        salePrice: PropTypes.number,
        reductions: PropTypes.array,
        discounts: PropTypes.array,
        isMismatch: PropTypes.bool,
    }),
};

export default PricingBreakdown;
